import { createFileRoute } from "@tanstack/react-router";
import { requireFeature } from "@/lib/feature-gate";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import type { DateRange } from "react-day-picker";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { DateSelector } from "@/components/crud/filters/DateSelector";
import { MultiSelectFilter } from "@/components/crud/filters/MultiSelectFilter";
import {
  timeSheetStatusFilterOptions,
  type TimeSheetStatusFilter,
} from "@/lib/timesheet-status-filter";
import SelectUser from "@/components/reusable-components/select-user";
import { TimeSheetTable } from "@/components/crud/table/TimeSheetTable";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import { useFetchSubordinates } from "@/hooks/api/team/useFetchSubordinates";
import { useDebounce } from "@/hooks/use-debounce";
import useFetchTeamTimeSheets from "@/hooks/api/timesheet/useFetchTeamTimeSheets";
import type { TimeSheetOrdering } from "@/hooks/api/timesheet/useFetchTimeSheets";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { nowInOrgTimezone, dayjs } from "@/lib/date-utils";

export const Route = createFileRoute("/supervisor/_layout/team-timesheet")({
  beforeLoad: () => requireFeature("attendance"),
  component: TeamTimesheetPage,
});

function TeamTimesheetPage() {
  const [showFilters, setShowFilters] = useState(false);
  // Default to today, matching the admin timesheet screen.
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: nowInOrgTimezone().startOf("day").toDate(),
    to: nowInOrgTimezone().toDate(),
  });

  // Team-member filter, bound the same way the admin page binds SelectUser.
  const { setValue, watch, control } = useForm<{ user_id?: number }>({
    defaultValues: { user_id: undefined },
  });
  const watchedUserId = watch("user_id");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<TimeSheetStatusFilter[]>([]);

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        date: SortDirection;
        created_at: SortDirection;
        expected_check_in: SortDirection;
      },
      TimeSheetOrdering
    >({
      date: null,
      created_at: null,
      expected_check_in: null,
    });

  // The team roster feeds the member picker — the same list /supervisor/team
  // shows. Deliberately not the users autocomplete the admin page uses: that
  // endpoint is `IsAdminUser`, so a supervisor gets a 403 from it, and for the
  // HR users who *can* call it, it would offer the whole org rather than the
  // team.
  const [debouncedMemberSearch] = useDebounce(searchQuery, 500);
  const subordinatesQuery = useFetchSubordinates({
    page_size: 100,
    search: debouncedMemberSearch || undefined,
  });
  const teamMembers = subordinatesQuery.data?.results ?? [];

  const timeSheetsQuery = useFetchTeamTimeSheets({
    page,
    page_size: pageSize,
    ordering: getApiOrdering(),
    user: watchedUserId !== undefined ? String(watchedUserId) : undefined,
    start_date: dateRange?.from
      ? dayjs(dateRange.from).format("YYYY-MM-DD")
      : undefined,
    end_date: dateRange?.to
      ? dayjs(dateRange.to).format("YYYY-MM-DD")
      : undefined,
    status: statusFilter.length ? statusFilter : undefined,
  });

  const timeSheets = timeSheetsQuery.data?.results ?? [];
  const count = timeSheetsQuery.data?.count ?? 0;

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [ordering, dateRange, watchedUserId, statusFilter, setPage]);

  const totalPages = useMemo(
    () => (count ? Math.ceil(count / pageSize) : 1),
    [count, pageSize],
  );

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const resetFilters = () => {
    setOrdering({ date: null, created_at: null, expected_check_in: null });
    setDateRange(undefined);
    setValue("user_id", undefined);
    setSearchQuery("");
    setStatusFilter([]);
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="My Team Timesheet"
          description="Attendance records for everyone who reports to you."
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          isLoading={timeSheetsQuery.isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={timeSheetsQuery.isFetching}
        >
          <Controller
            name="user_id"
            control={control}
            render={() => (
              <SelectUser
                watchedUserId={watchedUserId}
                users={teamMembers}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                setValue={setValue}
                isLoading={subordinatesQuery.isFetching}
              />
            )}
          />

          <MultiSelectFilter
            placeholder="Filter by status"
            options={timeSheetStatusFilterOptions}
            values={statusFilter}
            onChange={setStatusFilter}
          />

          <DateSelector
            value={dateRange}
            onApply={(range) => setDateRange(range)}
            onClear={() => setDateRange(undefined)}
            label="Filter by date range"
          />
        </FiltersPanel>

        <TimeSheetTable
          timesheets={timeSheets}
          isLoading={timeSheetsQuery.isPending}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
          onRefresh={() => timeSheetsQuery.refetch()}
          hideDirectAdjustment
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={timeSheetsQuery.isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </div>
    </SidebarInset>
  );
}
